import { useQuery } from "@tanstack/react-query";
import { calculateQLEEffectiveDate } from "@shared/utils/qleTypes";
import { parseDateLocal } from "@/lib/utils";

export type EnrollmentState =
  | "open_enrollment"
  | "new_hire"
  | "qualifying_event"
  | "closed";

export interface UseEnrollmentStatusResult {
  enrollmentState: EnrollmentState;
  canMakeChanges: boolean;
  // OE
  isOpenEnrollmentActive: boolean;
  hasCompletedOpenEnrollment: boolean;
  oeWindow: any | null;
  oeProgressRow: any | null;
  oeProgressKey: string | null;
  // New Hire
  isNewHireWindowOpen: boolean;
  newHireWindow: any | null;
  // QLE
  hasActiveQLE: boolean;
  qualifyingEvents: any | null;
  qleEffectiveDate: Date | null;
  qleEventDate: Date | null;
}

export function useEnrollmentStatus(
  clientId: number | string | undefined,
  employeeId: number | string | undefined
): UseEnrollmentStatusResult {
  // Open Enrollment window for this employee's client
  const { data: oeWindow } = useQuery<any>({
    queryKey: ["/api/clients", clientId, "open-enrollment"],
    enabled: Boolean(clientId),
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/open-enrollment`);
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Progress key is the OE window's startDate
  const oeProgressKey = oeWindow?.startDate || null;

  // Employee's Open Enrollment progress for current window (if any)
  const { data: oeProgressRow } = useQuery<any>({
    queryKey: [
      "/api/clients",
      clientId,
      "open-enrollment",
      "progress",
      oeProgressKey,
      employeeId,
    ],
    enabled:
      Boolean(clientId) &&
      Boolean(oeProgressKey) &&
      Boolean(employeeId),
    queryFn: async () => {
      const res = await fetch(
        `/api/clients/${clientId}/open-enrollment/progress?startDate=${oeProgressKey}`
      );
      if (!res.ok) return null;
      const rows = (await res.json()) as any[];
      return (rows || []).find((r) => r.employeeId === employeeId) || null;
    },
  });

  // Fetch new hire enrollment window
  const { data: newHireWindow } = useQuery<any>({
    queryKey: ["/api/employees", employeeId, "new-hire-window"],
    enabled: Boolean(employeeId),
    queryFn: async () => {
      const res = await fetch(
        `/api/employees/${employeeId}/new-hire-window`
      );
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Fetch qualifying events status
  const { data: qualifyingEvents } = useQuery<any>({
    queryKey: ["/api/employees/benefits/qualifying-events"],
    queryFn: async () => {
      const res = await fetch("/api/employees/benefits/qualifying-events", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Derived booleans
  const isOpenEnrollmentActive = Boolean(
    oeWindow &&
      new Date() >= (parseDateLocal(oeWindow.startDate) ?? new Date(0)) &&
      new Date() <= (parseDateLocal(oeWindow.endDate) ?? new Date(0))
  );

  const hasCompletedFromProgress = oeProgressRow?.status === "complete";

  // Enrollment-based fallback: check actual enrollments when progress table doesn't show "complete"
  const { data: employeeEnrollmentsForOeCheck } = useQuery<any[]>({
    queryKey: ["/api/employees", employeeId, "enrollments-oe-fallback"],
    enabled: Boolean(employeeId) && Boolean(oeProgressKey) && isOpenEnrollmentActive && !hasCompletedFromProgress,
    staleTime: 30000,
    queryFn: async () => {
      const res = await fetch(`/api/employees/${employeeId}/enrollments`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  let hasCompletedFromEnrollments = false;
  if (!hasCompletedFromProgress && oeProgressKey && Array.isArray(employeeEnrollmentsForOeCheck)) {
    const effDateStr = String(oeProgressKey).split('T')[0];
    const oeRows = employeeEnrollmentsForOeCheck.filter((e: any) => {
      const src = String(e.enrollmentSource || "").toLowerCase();
      if (src !== "open_enrollment") return false;
      if (!e.effectiveDate) return false;
      return String(e.effectiveDate).split('T')[0] === effDateStr;
    });
    hasCompletedFromEnrollments = oeRows.some(
      (e: any) => String(e.status || "").toLowerCase() === "active"
    );
  }

  const hasCompletedOpenEnrollment = hasCompletedFromProgress || hasCompletedFromEnrollments;

  const isNewHireWindowOpen = Boolean(newHireWindow?.isWindowOpen);

  const hasActiveQLE = Boolean(qualifyingEvents?.canMakeChanges);

  const qleEventDate = hasActiveQLE && qualifyingEvents?.activeEvent?.eventDate
    ? parseDateLocal(qualifyingEvents.activeEvent.eventDate)
    : null;

  const qleEffectiveDate = qleEventDate
    ? calculateQLEEffectiveDate(qleEventDate)
    : null;

  // Priority: New hire wins over OE (employee must complete initial enrollment).
  // QLE only applies when neither new hire nor OE is active.
  let enrollmentState: EnrollmentState = "closed";
  if (isNewHireWindowOpen) {
    enrollmentState = "new_hire";
  } else if (isOpenEnrollmentActive) {
    enrollmentState = "open_enrollment";
  } else if (hasActiveQLE) {
    enrollmentState = "qualifying_event";
  }

  const canMakeChanges = enrollmentState !== "closed";

  return {
    enrollmentState,
    canMakeChanges,
    isOpenEnrollmentActive,
    hasCompletedOpenEnrollment,
    oeWindow: oeWindow ?? null,
    oeProgressRow: oeProgressRow ?? null,
    oeProgressKey,
    isNewHireWindowOpen,
    newHireWindow: newHireWindow ?? null,
    hasActiveQLE,
    qualifyingEvents: qualifyingEvents ?? null,
    qleEffectiveDate,
    qleEventDate,
  };
}
